/**
 * Payment Checkout Modal
 * Main payment interface that integrates all payment methods
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { CardPaymentForm } from './CardPaymentForm';
import { ApplePayButton } from './ApplePayButton';
import { GooglePayButton } from './GooglePayButton';
import { ACHPaymentForm } from './ACHPaymentForm';
import { CoinflowHostedCheckout } from './CoinflowHostedCheckout';
import { ThreeDSChallengeModal } from './ThreeDSChallengeModal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { X, CreditCard, ArrowLeft } from 'lucide-react';
import { PackageSummaryCard } from './PackageSummaryCard';
import { getPaymentError } from '@/lib/payment-errors';
import { getDeviceId } from '@/lib/nsure';
import { COINFLOW_PRODUCT_TYPE, COINFLOW_PRODUCT_DESCRIPTION, CoinflowTokenization } from '@/lib/coinflow-tokenization';
import { apiClient } from '@/lib/api-client';
import { CoinflowCvvOnlyInput, CardType, CoinflowCardTokenResponse, CoinflowEnvs } from '@coinflowlabs/react';
import { paymentStore } from '@/stores/paymentStore';
import { useLogoSrc } from '@/hooks/useLogoSrc';

type PaymentMethodType = 'card' | 'apple_pay' | 'google_pay' | 'ach' | 'coinflow_iframe' | 'cashapp' | 'crypto';

type CheckoutSessionError = { message: string; kind: 'maintenance' | 'error' };

/** Shared error/maintenance state for checkout session failures */
function CheckoutSessionErrorState({
  error,
  onClose,
  onRetry,
  compact,
}: {
  error: CheckoutSessionError;
  onClose: () => void;
  onRetry: () => void;
  compact?: boolean;
}) {
  const textSm = compact ? 'text-[10px] lg:text-sm' : 'text-sm';
  const textXs = compact ? 'text-[9px] lg:text-xs' : 'text-xs';
  const py = compact ? 'py-8 lg:py-12' : 'py-8';

  if (error.kind === 'maintenance') {
    return (
      <div className={`flex flex-col items-center justify-center ${py} text-center`}>
        <div className="text-amber-500 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className={`${textSm} font-medium mb-2`}>
          Purchases Temporarily Unavailable
        </p>
        <p className={`${textXs} text-muted-foreground mb-4 max-w-xs`}>
          We're performing scheduled maintenance on our payment system. Please check back shortly.
        </p>
        <Button variant="outline" size="sm" onClick={onClose} className={textSm}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center ${py} text-center`}>
      <div className="text-destructive mb-4">
        <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <p className={`${textSm} font-medium text-destructive mb-2`}>
        Failed to initialize checkout
      </p>
      <p className={`${textXs} text-muted-foreground mb-4 max-w-xs`}>
        {error.message}
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onClose} className={textSm}>
          Cancel
        </Button>
        <Button variant="default" size="sm" onClick={onRetry} className={textSm}>
          Try Again
        </Button>
      </div>
    </div>
  );
}

interface PaymentCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  packageId?: string;  // For coin_package purchases
  scheduledOfferId?: string;  // For scheduled_offer purchases
  packageTitle: string;
  amount: number | string; // in dollars (may be string from API) - discounted price if promo code applied
  originalAmount?: number | string; // original price before discount
  gcAmount?: number; // GC included in purchase
  scAmount?: number; // SC included in purchase
  gcBonusAmount?: number; // Bonus GC
  scBonusAmount?: number; // Bonus SC
  promoCode?: string;
  promoRewardType?: string;
  promoRewardValue?: number;
  promoBonusGC?: number;
  promoBonusSC?: number;
  onSuccess: (data: { isFirstPurchase?: boolean }) => void;
  /** If true, show only Coinflow Hosted Checkout without payment method selector */
  coinflowOnly?: boolean;
  /** Checkout type for hosted checkout: 'card' (default) shows card/Apple Pay/Google Pay, 'crypto' shows only crypto */
  checkoutType?: 'card' | 'crypto';
}

export const PaymentCheckoutModal: React.FC<PaymentCheckoutModalProps> = ({
  open,
  onClose,
  packageId,
  scheduledOfferId,
  packageTitle,
  amount,
  originalAmount,
  gcAmount,
  scAmount,
  gcBonusAmount,
  scBonusAmount,
  promoCode,
  promoRewardType,
  promoRewardValue,
  promoBonusGC,
  promoBonusSC,
  onSuccess,
  coinflowOnly = false,
  checkoutType = 'card',
}) => {
  // Use either packageId or scheduledOfferId for API calls
  const itemId = packageId || scheduledOfferId || '';
  const isScheduledOffer = !!scheduledOfferId;
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType | null>(null);
  const [selectedProcessor, setSelectedProcessor] = useState<string>('coinflow');
  const [selectedSavedCard, setSelectedSavedCard] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh of method selector
  const [challengeData, setChallengeData] = useState<{
    transactionId: string;
    creq: string;
    url: string;
  } | null>(null);
  const [needsCvvRevalidation, setNeedsCvvRevalidation] = useState(false);
  const [cvvInputReady, setCvvInputReady] = useState(false);
  const [refreshedToken, setRefreshedToken] = useState<string | null>(null); // Store refreshed token from CVV input
  const coinflowCvvOnlyRef = useRef<{ getToken(): Promise<CoinflowCardTokenResponse> } | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  
  // Checkout session - single transaction for all payment methods
  const [checkoutSessionId, setCheckoutSessionId] = useState<number | null>(null);
  const [checkoutSessionLoading, setCheckoutSessionLoading] = useState(true); // Start true to prevent rendering before session is ready
  const [checkoutSessionError, setCheckoutSessionError] = useState<{
    message: string;
    kind: 'maintenance' | 'error';
  } | null>(null);
  const checkoutSessionInitialized = useRef(false);
  const paymentCompletedRef = useRef(false);
  
  // Cash App checkout state
  const [cashAppData, setCashAppData] = useState<{
    link: string;
    transactionId: number;
    expiresAt?: string;
  } | null>(null);

  // Helper function to convert string card type to CardType enum
  // CoinflowCvvOnlyInput requires the CardType enum, not a string
  const getCardTypeEnum = (cardTypeString: string | undefined): CardType => {
    const normalizedType = (cardTypeString || '').toUpperCase();
    switch (normalizedType) {
      case 'VISA':
        return CardType.VISA;
      case 'MASTERCARD':
      case 'MC':
        return CardType.MASTERCARD;
      case 'AMEX':
      case 'AMERICAN EXPRESS':
      case 'AMERICANEXPRESS':
        return CardType.AMEX;
      case 'DISCOVER':
        return CardType.DISCOVER;
      default:
        // Default to VISA if unknown - most common card type
        return CardType.VISA;
    }
  };

  // Swipe to close state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);

  const logoSrc = useLogoSrc();
  
  // Ensure amount is a number (handle cases where API returns string)
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
  const amountInCents = Math.round(numericAmount * 100);
  
  // Calculate total GC and SC amounts for display
  const totalGC = (Number(gcAmount) || 0) + (Number(gcBonusAmount) || 0);
  const totalSC = (Number(scAmount) || 0) + (Number(scBonusAmount) || 0);

  // Swipe to close handlers
  const handleDragHandleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    dragStartY.current = touch.clientY;
    setIsDragging(true);
  };

  const handleDragHandleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleDragHandleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.stopPropagation();
    
    const threshold = 100;
    if (dragY > threshold) {
      onClose();
    } else {
      setDragY(0);
    }
    
    setIsDragging(false);
    dragStartY.current = 0;
  };

  const handleHeaderTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch.clientY < 60) {
      handleDragHandleTouchStart(e);
    }
  };

  useEffect(() => {
    if (!open) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [open]);

  // Check if CVV input is ready (refs don't trigger re-renders, so we poll)
  useEffect(() => {
    if (!needsCvvRevalidation) {
      setCvvInputReady(false);
      return;
    }

    const checkRef = () => {
      const hasRef = !!coinflowCvvOnlyRef.current;
      const hasGetToken = coinflowCvvOnlyRef.current ? typeof coinflowCvvOnlyRef.current.getToken === 'function' : false;
      
      if (hasRef && hasGetToken) {
        setCvvInputReady(true);
        return true; // Ref is ready
      }
      return false; // Ref not ready yet
    };

    // Check immediately
    if (checkRef()) {
      return; // Already ready, no need to poll
    }

    // Poll every 100ms to check if ref is ready (stop once ready)
    const interval = setInterval(() => {
      if (checkRef()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [needsCvvRevalidation]);
  const merchantId = import.meta.env.VITE_COINFLOW_MERCHANT_ID || '';

  // Refresh available methods when modal opens
  useEffect(() => {
    if (open) {
      setSelectedMethod(null);
      setSelectedSavedCard(null);
      setChallengeData(null);
      setNeedsCvvRevalidation(false);
      setCvvInputReady(false); // Reset ready state when modal opens
      setRefreshedToken(null); // Reset refreshed token when modal opens
      setRefreshKey((prev) => prev + 1);
      paymentCompletedRef.current = false;
    }
  }, [open]);

  // Create checkout session when modal opens - single transaction for all payment methods
  // IMPORTANT: Even in coinflowOnly mode, we create a session to prevent duplicate transactions
  // when the CoinflowHostedCheckout component remounts
  useEffect(() => {
    if (!open || checkoutSessionInitialized.current) return;
    
    const initCheckoutSession = async () => {
      try {
        checkoutSessionInitialized.current = true;
        setCheckoutSessionLoading(true);
        setCheckoutSessionError(null);
        
        const response = await apiClient.post('/payments/initiate-checkout-session', {
          ...(isScheduledOffer ? { scheduledOfferId: itemId } : { packageId: itemId }),
          amount: amountInCents,
          // Include promo code info for Coinflow hosted checkout flow
          promoCode: promoCode || undefined,
          promoBonusGc: promoBonusGC || 0,
          promoBonusSc: promoBonusSC || 0,
        });
        
        if (!response.transactionId) {
          throw new Error('No transaction ID returned from checkout session');
        }
        
        setCheckoutSessionId(response.transactionId);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to initialize checkout';
        const isMaintenance = error?.response?.data?.error === 'PROCESSOR_UNAVAILABLE';
        setCheckoutSessionError({ message: errorMessage, kind: isMaintenance ? 'maintenance' : 'error' });
        // Note: checkoutSessionId remains null, which will prevent wallet buttons from rendering
        // This prevents the duplicate transaction issue
      } finally {
        setCheckoutSessionLoading(false);
      }
    };
    
    initCheckoutSession();
  }, [open, coinflowOnly, isScheduledOffer, itemId, amountInCents, promoCode, promoBonusGC, promoBonusSC]);

  // Cleanup on unmount - abandon checkout session if not completed
  useEffect(() => {
    return () => {
      if (checkoutSessionId && !paymentCompletedRef.current) {
        apiClient.post('/payments/abandon-checkout', { transactionId: checkoutSessionId }).catch(() => {});
      }
      // Reset for next open
      checkoutSessionInitialized.current = false;
    };
  }, [checkoutSessionId]);

  // Reset session when modal closes
  useEffect(() => {
    if (!open) {
      // Delay reset to allow cleanup effect to run first
      const timeout = setTimeout(() => {
        setCheckoutSessionId(null);
        setCheckoutSessionLoading(true); // Reset to true for next open
        setCheckoutSessionError(null);
        checkoutSessionInitialized.current = false;
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  const handleMethodSelect = (method: PaymentMethodType, processorId: string) => {
    // When Coinflow Checkout is selected, switch to coinflowOnly mode
    // This opens the raw Coinflow iframe without embedding it here
    if (method === 'coinflow_iframe') {
      paymentStore.switchToCoinflowOnly();
      return;
    }
    setSelectedMethod(method);
    setSelectedProcessor(processorId);
  };

  const handleCashAppCheckout = async () => {
    setIsProcessing(true);
    try {
      const response = await apiClient.post('/payments/cashapp-checkout', {
        packageId: isScheduledOffer ? undefined : itemId,
        scheduledOfferId: isScheduledOffer ? itemId : undefined,
        amount: amountInCents,
        packageTitle,
        deviceId: getDeviceId(),
        promoCode: promoCode || undefined,
        promoBonusGc: promoBonusGC || 0,
        promoBonusSc: promoBonusSC || 0,
      });

      if (response.cashAppLink) {
        // Store Cash App link data to show QR code and buttons
        setCashAppData({
          link: response.cashAppLink,
          transactionId: response.transactionId,
          expiresAt: response.expiresAt,
        });
        setIsProcessing(false);
      } else {
        throw new Error('Failed to get Cash App checkout link');
      }
    } catch (error: any) {
      setIsProcessing(false);
      const { message } = getPaymentError(error);
      toast.error('Cash App checkout failed', {
        description: message,
      });
    }
  };

  const handleOpenCashAppLink = () => {
    if (cashAppData?.link) {
      window.open(cashAppData.link, '_blank');
    }
  };

  const handleCopyCashAppLink = async () => {
    if (cashAppData?.link) {
      try {
        await navigator.clipboard.writeText(cashAppData.link);
        toast.success('Link copied!', {
          description: 'Cash App link copied to clipboard',
        });
      } catch {
        toast.error('Failed to copy link');
      }
    }
  };

  const handleCashAppBack = () => {
    setCashAppData(null);
  };

  const handleCryptoCheckout = () => {
    // Crypto checkout uses the same hosted checkout flow but with checkoutType: 'crypto'
    // This opens the Coinflow iframe with only crypto payment option
    paymentStore.switchToCoinflowOnly('crypto');
  };

  const handlePaymentSuccess = (_paymentId: string, data: { isFirstPurchase?: boolean } = {}) => {
    paymentCompletedRef.current = true; // Mark payment as completed to prevent abandon on unmount
    setIsProcessing(false);
    setRefreshedToken(null); // Clear refreshed token after successful payment
    // Refresh saved cards after successful payment
    setRefreshKey((prev) => prev + 1);
    // Note: onSuccess triggers the success animation in PaymentProvider, which auto-closes after 2s
    onSuccess(data);
  };

  const handlePaymentError = (error: string | Error) => {
    setIsProcessing(false);
    const { message } = getPaymentError(error);
    toast.error('Payment failed', {
      description: message,
    });
  };

  const handleBack = () => {
    setSelectedMethod(null);
    setSelectedSavedCard(null);
    setChallengeData(null);
  };

  const handleSavedCardSelect = (savedCard: any) => {
    // Select saved card - try checkout first, show CVV only if 410 error (validation expired)
    setSelectedSavedCard(savedCard);
    setNeedsCvvRevalidation(false); // Don't show CVV input upfront - try checkout first
  };

  const handleSavedCardCheckout = async () => {
    if (!selectedSavedCard) {
      handlePaymentError(new Error('Please select a saved card'));
      return;
    }
    
    if (!selectedSavedCard.token) {
      handlePaymentError(new Error('Saved card token is missing. Please try selecting the card again.'));
      return;
    }

    setIsProcessing(true);
    try {
      // Use token directly from saved card (no CVV input needed)
      // Process checkout with token directly from API response
      // Token is sufficient - backend will use /checkout/token/{merchantId} endpoint
      const response = await apiClient.post('/payments/checkout', {
        packageId: isScheduledOffer ? undefined : itemId,
        scheduledOfferId: isScheduledOffer ? itemId : undefined,
        paymentMethod: selectedSavedCard.method_type === 'card' ? 'card' : 'ach',
        token: selectedSavedCard.token, // Use token directly from customer.cards[].token
        customerInfo: {
          email: '', // Will be filled from user context on backend
          // Billing address will be fetched from user_profile on backend if not provided
        },
        deviceId: getDeviceId(),
        promoCode: promoCode || undefined,
        promoBonusGc: promoBonusGC || 0,
        promoBonusSc: promoBonusSC || 0,
        chargebackProtectionData: [
          {
            productName: 'package',
            productType: COINFLOW_PRODUCT_TYPE,
            quantity: 1,
            rawProductData: {
              description: COINFLOW_PRODUCT_DESCRIPTION,
              packageId: itemId,
              packageTitle,
              amount,
            },
          },
        ],
        authentication3DS: CoinflowTokenization.get3DSParams(), // Required for 3DS authentication
      });

      if (response.externalPaymentId) {
        handlePaymentSuccess(response.externalPaymentId, { isFirstPurchase: response.isFirstPurchase });
      } else if (response.requiresAction) {
        // Handle 3DS challenge
        const actionData = response.actionData || response;
        if (actionData?.type === '3ds_challenge') {
          setChallengeData({
            transactionId: actionData.transactionId,
            creq: actionData.creq,
            url: actionData.url,
          });
          setIsProcessing(false);
          return;
        } else {
          handlePaymentError(new Error('Payment requires additional action'));
        }
      } else {
        handlePaymentError(new Error('Payment failed'));
      }
    } catch (error: any) {
      // Check for 410 error - CVV validation expired, need to revalidate
      // Check multiple possible locations for status code
      const statusCode = error.status || error.statusCode || error.response?.status || error.response?.statusCode;
      
      if (statusCode === 410) {
        // Use setTimeout to ensure state update happens after current execution
        setTimeout(() => {
          setNeedsCvvRevalidation(true);
          setIsProcessing(false);
        }, 0);
        
        // Also set immediately
        setNeedsCvvRevalidation(true);
        setIsProcessing(false);
        return; // Don't call handlePaymentError, show CVV input instead
      }
      
      // Check for 3DS challenge in error response (412 status)
      if (error.status === 412 || error.response?.requiresAction) {
        const actionData = error.response?.actionData || error.response;
        
        if (actionData?.type === '3ds_challenge') {
          setChallengeData({
            transactionId: actionData.transactionId,
            creq: actionData.creq,
            url: actionData.url,
          });
          setIsProcessing(false);
          return; // Don't call handlePaymentError
        }
      }
      
      handlePaymentError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChallengeComplete = useCallback(
    async (transactionId: string) => {
      if (!selectedSavedCard) {
        handlePaymentError(new Error('Saved card information missing'));
        return;
      }

      // Use refreshed token if available (from CVV input), otherwise fall back to saved card token
      const tokenToUse = refreshedToken || selectedSavedCard.token;
      if (!tokenToUse) {
        handlePaymentError(new Error('Payment token is missing'));
        return;
      }

      setIsProcessing(true);
      setChallengeData(null); // Close challenge modal

      try {
        // Complete checkout with challenge transactionId using refreshed token from CVV input
        const response = await apiClient.post('/payments/checkout', {
          packageId: isScheduledOffer ? undefined : itemId,
          scheduledOfferId: isScheduledOffer ? itemId : undefined,
          paymentMethod: selectedSavedCard.method_type === 'card' ? 'card' : 'ach',
          token: tokenToUse, // Use refreshed token from CVV input if available
          customerInfo: {
            email: '', // Will be filled from user context on backend
          },
          deviceId: getDeviceId(),
          challengeTransactionId: transactionId, // For completing 3DS challenge
          promoCode: promoCode || undefined,
          promoBonusGc: promoBonusGC || 0,
          promoBonusSc: promoBonusSC || 0,
          chargebackProtectionData: [
            {
              productName: 'package',
              productType: COINFLOW_PRODUCT_TYPE,
              quantity: 1,
              rawProductData: {
                description: COINFLOW_PRODUCT_DESCRIPTION,
                packageId: itemId,
                packageTitle,
                amount,
              },
            },
          ],
          // Note: authentication3DS not needed when completing challenge - challengeTransactionId is sufficient
        });

        if (response.externalPaymentId) {
          handlePaymentSuccess(response.externalPaymentId, { isFirstPurchase: response.isFirstPurchase });
        } else {
          handlePaymentError(new Error('Payment completion failed'));
        }
      } catch (error: any) {
        handlePaymentError(error);
      } finally {
        setIsProcessing(false);
      }
    },
    [selectedSavedCard, refreshedToken, itemId, isScheduledOffer, packageTitle, promoCode, amount],
  );

  const handleCvvRevalidation = async () => {
    if (!selectedSavedCard) {
      handlePaymentError(new Error('No payment method selected'));
      return;
    }

    if (!coinflowCvvOnlyRef.current) {
      handlePaymentError(new Error('CVV input not available. Please refresh the page and try again.'));
      return;
    }

    setIsProcessing(true);
    
    try {
      // Get refreshed token from CoinflowCvvOnlyInput (includes CVV)
      // Wait longer to ensure the component is fully mounted and the CVV field is ready
      // The Coinflow SDK might need time to initialize the iframe and validate the CVV input
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Double-check ref is still available after delay
      if (!coinflowCvvOnlyRef.current) {
        throw new Error('CVV input component is not available. Please try again.');
      }
      
      if (typeof coinflowCvvOnlyRef.current.getToken !== 'function') {
        throw new Error('CVV input component is not ready. Please try again.');
      }
      
      // Check if CVV field is visible and ready
      // The Coinflow SDK uses an iframe, so we can't directly access the input
      // But we can check if the wrapper is visible and wait a bit more for the iframe to be ready
      const cvvWrapper = document.querySelector('.coinflow-cvv-wrapper');
      if (cvvWrapper) {
        // Wait for the iframe to fully load and be ready
        // Coinflow SDK might need time to initialize the iframe and validate the CVV
        const iframe = cvvWrapper.querySelector('iframe');
        if (iframe) {
          // Wait for iframe to load if it hasn't already
          await new Promise((resolve) => {
            if (iframe.contentWindow) {
              // Iframe already loaded
              resolve(undefined);
            } else {
              // Wait for iframe to load
              iframe.onload = () => {
                resolve(undefined);
              };
              // Timeout after 3 seconds
              setTimeout(() => {
                resolve(undefined);
              }, 3000);
            }
          });
        }
        
        // Wait additional time for Coinflow SDK to fully initialize and be ready to accept CVV
        // IMPORTANT: The user should have already entered the CVV by this point
        // The SDK might need the CVV to be entered AND the field to be interacted with (focused/blurred)
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      let tokenResponse;
      try {
        // Set up TokenEx error listener to catch "Token does not exist" errors immediately
        let tokenExError: { error: string; referenceNumber?: string } | null = null;
        
        // Listen for window messages from TokenEx iframe
        const messageListener = (event: MessageEvent) => {
          try {
            if (event.data && typeof event.data === 'object') {
              if (event.data.event === 'error' && event.data.data?.error) {
                tokenExError = {
                  error: event.data.data.error,
                  referenceNumber: event.data.data.referenceNumber,
                };
              }
            }
          } catch (e) {
            // Ignore errors
          }
        };
        window.addEventListener('message', messageListener);
        
        // Add timeout wrapper to prevent hanging (reduced to 10 seconds for faster feedback)
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            window.removeEventListener('message', messageListener);
            
            // Check if we detected a TokenEx error
            if (tokenExError) {
              if (tokenExError.error.includes('Token does not exist') || tokenExError.error.includes('token') && tokenExError.error.includes('not exist')) {
                reject(new Error('The saved card token is not recognized by Coinflow. This card may have expired or been deleted. Please try using a different payment method or add a new card.'));
                return;
              }
            }
            
            reject(new Error('getToken() timed out after 10 seconds. Please ensure you have entered a valid CVV (3-4 digits) and try again.'));
          }, 10000);
        });
        
        // Call getToken and race with timeout
        // Note: getToken() will reject if CVV is invalid, but it might hang if there's a network issue
        const getTokenPromise = coinflowCvvOnlyRef.current.getToken()
          .then((result) => {
            window.removeEventListener('message', messageListener);
            return result;
          })
          .catch((error: any) => {
            window.removeEventListener('message', messageListener);
            
            // Check if we detected a TokenEx error before the promise rejected
            if (tokenExError) {
              if (tokenExError.error.includes('Token does not exist') || 
                  (tokenExError.error.includes('token') && tokenExError.error.includes('not exist'))) {
                throw new Error('The saved card token is not recognized by Coinflow. This card may have expired or been deleted. Please try using a different payment method or add a new card.');
              }
            }
          
          // Check for "Token does not exist" error from TokenEx
          const errorMessage = error?.message || '';
          
          if (errorMessage.includes('Token does not exist') || 
              (errorMessage.includes('token') && errorMessage.includes('not exist')) ||
              errorMessage.includes('Unable to get token')) {
            throw new Error('The saved card token is not recognized. Please try using a different payment method or add a new card.');
          }
          
          // Re-throw to be caught by outer catch
          throw error;
        });
        
        // Check for TokenEx errors periodically while waiting
        const errorCheckInterval = setInterval(() => {
          if (tokenExError) {
            if (tokenExError.error.includes('Token does not exist') || 
                (tokenExError.error.includes('token') && tokenExError.error.includes('not exist'))) {
              clearInterval(errorCheckInterval);
              window.removeEventListener('message', messageListener);
              throw new Error('The saved card token is not recognized by Coinflow. This card may have expired or been deleted. Please try using a different payment method or add a new card.');
            }
          }
        }, 500);
        
        try {
          tokenResponse = await Promise.race([
            getTokenPromise,
            timeoutPromise,
          ]) as CoinflowCardTokenResponse;
          
          // Clear error check interval on success
          clearInterval(errorCheckInterval);
        } catch (error) {
          // Clear error check interval on error
          clearInterval(errorCheckInterval);
          throw error;
        }
      } catch (tokenError: any) {
        // Check if it's a timeout
        if (tokenError.message?.includes('timed out')) {
          throw new Error('CVV tokenization timed out. Please ensure you have entered a valid CVV (3 digits for Visa/Mastercard, 4 digits for American Express) and try again.');
        }
        
        // Check for "Token does not exist" error from TokenEx
        // This happens when the saved card token is not recognized by Coinflow's tokenization service
        if (tokenError.message?.includes('Token does not exist') || 
            tokenError.message?.includes('token') && tokenError.message?.includes('not exist') ||
            tokenError.message?.includes('Unable to get token')) {
          throw new Error('The saved card token is not recognized. This card may have expired or been deleted. Please try using a different payment method or add a new card.');
        }
        
        // Check for specific validation errors from Coinflow
        if (tokenError.message?.includes('CVV') || tokenError.message?.includes('cvv') || tokenError.message?.includes('invalid')) {
          throw new Error(`CVV validation failed: ${tokenError.message}. Please check that your CVV is correct and try again.`);
        }
        
        throw new Error(`Failed to get token: ${tokenError.message || 'Unknown error'}`);
      }
      
      if (!tokenResponse || !tokenResponse.token) {
        throw new Error('Failed to get refreshed token with CVV');
      }
      
      const refreshedToken = tokenResponse.token;
      setRefreshedToken(refreshedToken); // Store refreshed token for use in 3DS challenge completion

      // Retry payment with refreshed token
      // IMPORTANT: The refreshed token from CoinflowCvvOnlyInput.getToken() includes CVV
      // and should be used with the /checkout/token/{merchantId} endpoint (same as saved cards)
      // The backend will detect this is a refreshed token and handle it appropriately
      const response = await apiClient.post('/payments/checkout', {
        packageId: isScheduledOffer ? undefined : itemId,
        scheduledOfferId: isScheduledOffer ? itemId : undefined,
        paymentMethod: selectedSavedCard.method_type === 'card' ? 'card' : 'ach',
        token: refreshedToken, // Refreshed token from CoinflowCvvOnlyInput (includes CVV)
        // IMPORTANT: This is a refreshed token with CVV, so backend should use /checkout/token endpoint (same as saved cards)
        // We'll indicate this by setting isRefreshedToken flag in metadata
        customerInfo: {
          email: '', // Will be filled from user context on backend
          // No cardExpYear/cardExpMonth - this tells backend this is not a new card
        },
        // Add flag to indicate this is a refreshed token (will be handled in backend)
        isRefreshedToken: true, // Flag for backend to use /checkout/token endpoint (same as saved cards)
        deviceId: getDeviceId(),
        promoCode: promoCode || undefined,
        promoBonusGc: promoBonusGC || 0,
        promoBonusSc: promoBonusSC || 0,
        chargebackProtectionData: [
          {
            productName: 'package',
            productType: COINFLOW_PRODUCT_TYPE,
            quantity: 1,
            rawProductData: {
              description: COINFLOW_PRODUCT_DESCRIPTION,
              packageId: itemId,
              packageTitle,
              amount,
            },
          },
        ],
        authentication3DS: CoinflowTokenization.get3DSParams(), // Required for 3DS authentication
      });

      if (response.externalPaymentId) {
        setNeedsCvvRevalidation(false); // Clear CVV revalidation state
        setRefreshedToken(null); // Clear refreshed token after successful payment
        handlePaymentSuccess(response.externalPaymentId, { isFirstPurchase: response.isFirstPurchase });
      } else if (response.requiresAction) {
        // Handle 3DS challenge
        const actionData = response.actionData || response;
        if (actionData?.type === '3ds_challenge') {
          setNeedsCvvRevalidation(false); // Clear CVV revalidation state
          setChallengeData({
            transactionId: actionData.transactionId,
            creq: actionData.creq,
            url: actionData.url,
          });
          setIsProcessing(false);
          return;
        } else {
          handlePaymentError(new Error('Payment requires additional action'));
        }
      } else {
        handlePaymentError(new Error('Payment failed'));
      }
    } catch (error: any) {
      // Check for 3DS challenge in error response
      if (error.status === 412 || error.response?.requiresAction) {
        const actionData = error.response?.actionData || error.response;
        if (actionData?.type === '3ds_challenge') {
          setNeedsCvvRevalidation(false); // Clear CVV revalidation state
          setChallengeData({
            transactionId: actionData.transactionId,
            creq: actionData.creq,
            url: actionData.url,
          });
          setIsProcessing(false);
          return;
        }
      }
      
      // Check for another 410 error (shouldn't happen, but handle it)
      if (error.status === 410 || error.response?.status === 410) {
        // Already showing CVV input, just show error message
        handlePaymentError(new Error('CVV validation failed. Please try again.'));
      } else {
        handlePaymentError(error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUseNewCard = () => {
    setSelectedSavedCard(null);
    setSelectedMethod('card');
  };

  // Cash App checkout view - show QR code and buttons
  if (cashAppData) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent 
          className="max-w-md w-full p-0 gap-0 border-0 bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] overflow-hidden [&>button]:hidden"
          aria-describedby={undefined}
        >
          <div className="flex flex-col p-6 space-y-6">
            {/* Header with back button */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCashAppBack}
                className="h-8 w-8 rounded-full bg-white/5 hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h2 className="text-xl font-bold text-white">Complete payment in Cash App</h2>
                <p className="text-sm text-white/60">
                  Click the button below or scan the QR code to complete your payment.
                </p>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex justify-center">
              <div className="bg-[#00D632] p-4 rounded-2xl">
                <div className="bg-white p-2 rounded-xl">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(cashAppData.link)}&bgcolor=ffffff&color=000000&margin=0`}
                    alt="Cash App QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-center text-white font-medium mt-2">Scan to pay</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleOpenCashAppLink}
                className="w-full h-12 bg-[#00D632] hover:bg-[#00B82B] text-white font-semibold rounded-xl"
              >
                Open Cash App Link
              </Button>
              <Button
                onClick={handleCopyCashAppLink}
                variant="outline"
                className="w-full h-12 bg-white/5 hover:bg-white/10 border-white/10 text-white font-medium rounded-xl"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Cash App Link
              </Button>
            </div>

            {/* Info text */}
            <p className="text-xs text-white/40 text-center">
              Your purchase will be confirmed automatically once payment is completed in Cash App.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Coinflow Hosted Checkout only mode: auto-opened when only coinflow_hosted method is enabled
  // Wait for checkout session to be created before rendering iframe to prevent duplicate transactions
  if (coinflowOnly) {
    // Show loading while checkout session is being created
    if (checkoutSessionLoading) {
      return (
        <Dialog open={open} onOpenChange={onClose}>
          <DialogContent 
            className="max-w-lg w-full p-0 gap-0 border-0 bg-background overflow-hidden [&>button]:hidden"
            aria-describedby={undefined}
          >
            <div className="flex flex-col items-center justify-center p-8 space-y-4 min-h-[300px]">
              <div className="relative">
                <div 
                  className="absolute inset-0 blur-2xl opacity-30"
                  style={{
                    background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
                    width: 160,
                    height: 160,
                    left: -40,
                    top: -40,
                  }}
                />
                <img
                  src={logoSrc}
                  alt="Gold Machine"
                  width={80}
                  height={80}
                  className="relative z-10 animate-pulse object-contain"
                  style={{ animationDuration: '2s' }}
                />
              </div>
              <p className="text-sm text-amber-400/80 font-medium">Initializing checkout...</p>
              <div className="flex items-center gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-amber-500/60"
                    style={{
                      animation: 'dot-pulse 1.2s infinite ease-in-out',
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
              </div>
              <style>{`
                @keyframes dot-pulse {
                  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                  40% { opacity: 1; transform: scale(1); }
                }
              `}</style>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    // Show error if checkout session failed
    if (checkoutSessionError) {
      return (
        <Dialog open={open} onOpenChange={onClose}>
          <DialogContent
            className="max-w-lg w-full p-4 gap-0 border-0 bg-background overflow-hidden [&>button]:hidden"
            aria-describedby={undefined}
          >
            <CheckoutSessionErrorState
              error={checkoutSessionError}
              onClose={onClose}
              onRetry={() => {
                checkoutSessionInitialized.current = false;
                setCheckoutSessionError(null);
                setCheckoutSessionLoading(true);
              }}
            />
          </DialogContent>
        </Dialog>
      );
    }

    // Render iframe only after checkout session is ready
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent 
          className="max-w-lg w-full p-0 gap-0 border-0 bg-transparent overflow-hidden [&>button]:hidden"
          aria-describedby={undefined}
        >
          <CoinflowHostedCheckout
            key={`coinflow-checkout-${checkoutType}-${checkoutSessionId}`}
            amount={amountInCents}
            packageId={isScheduledOffer ? undefined : itemId}
            scheduledOfferId={isScheduledOffer ? itemId : undefined}
            packageTitle={packageTitle}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onBack={onClose}
            checkoutSessionId={checkoutSessionId}
            promoCode={promoCode}
            promoBonusGC={promoBonusGC}
            promoBonusSC={promoBonusSC}
            checkoutType={checkoutType}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        ref={modalContentRef}
        className="!fixed !left-0 !top-0 !right-0 !bottom-0 !w-[100vw] !h-[100vh] !max-w-none !max-h-none lg:!left-[50%] lg:!top-[50%] lg:!right-auto lg:!bottom-auto lg:!w-[95vw] lg:!max-w-xl lg:!h-auto lg:!max-h-[90vh] m-0 p-0 rounded-none lg:rounded-3xl border-0 lg:border lg:border-white/[0.08] overflow-y-auto overflow-x-hidden !translate-x-0 !translate-y-0 lg:!translate-x-[-50%] lg:!translate-y-[-50%] flex flex-col pt-[env(safe-area-inset-top)] lg:pt-0 pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-0 dialog-content-base lg:shadow-2xl lg:shadow-black/60"
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          opacity: dragY > 0 ? Math.max(0.6, 1 - dragY / 400) : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(10, 15, 30, 0.98) 100%)',
          backdropFilter: 'blur(40px) saturate(150%)',
        }}
        aria-describedby={undefined}
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-black/20 pointer-events-none rounded-3xl" />
        
        {/* Drag Handle Indicator - Mobile & Tablet Only */}
        <div 
          className="lg:hidden pt-3 pb-2 flex justify-center touch-auto cursor-grab active:cursor-grabbing select-none relative z-10"
          onTouchStart={handleDragHandleTouchStart}
          onTouchMove={handleDragHandleTouchMove}
          onTouchEnd={handleDragHandleTouchEnd}
        >
          <div 
            className="w-10 h-1 bg-white/20 rounded-full transition-all duration-200"
            style={{
              opacity: isDragging ? 0.6 : 1,
              transform: isDragging ? 'scaleX(0.6)' : 'scaleX(1)',
              transition: isDragging ? 'none' : 'all 0.2s ease',
            }}
          />
        </div>

        {/* Close button - positioned at top right */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-3 top-3 lg:right-4 lg:top-4 h-8 w-8 lg:h-10 lg:w-10 rounded-full hover:bg-white/10 transition-colors z-20"
          aria-label="Close"
        >
          <X className="h-4 w-4 lg:h-5 lg:w-5 text-white/70" />
        </Button>
        <DialogHeader className="sr-only">
          <DialogTitle>Payment</DialogTitle>
        </DialogHeader>

        <div className="px-4 lg:px-6 pb-6 lg:pb-8 pt-8 lg:pt-10 flex-1 overflow-y-auto min-h-0 relative z-10">
          <div className="space-y-4 lg:space-y-6 max-w-full mx-auto">
            {/* Package Summary */}
            <PackageSummaryCard
                totalGC={totalGC}
                totalSC={totalSC}
                price={numericAmount}
                promoCode={promoCode}
                promoRewardType={promoRewardType}
                promoRewardValue={promoRewardValue}
                showPrice={false}
              />

          {/* 3DS Challenge Modal */}
          {challengeData && (
            <ThreeDSChallengeModal
              url={challengeData.url}
              creq={challengeData.creq}
              transactionId={challengeData.transactionId}
              onChallengeComplete={handleChallengeComplete}
              onClose={() => {
                setChallengeData(null);
                setSelectedSavedCard(null);
              }}
            />
          )}

            {/* Saved Card Confirmation */}
            {!challengeData && selectedSavedCard && !selectedMethod && (
              <div className="space-y-3 lg:space-y-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-7 lg:h-8 px-2 lg:px-3 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1" />
                  <span className="text-[10px] lg:text-sm font-medium">Back to payment methods</span>
                </Button>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 lg:p-5">
                  <div className="space-y-3 lg:space-y-4">
                    <div className="flex items-center gap-3 lg:gap-4">
                      <div className="h-10 w-10 lg:h-12 lg:w-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                        <CreditCard className="h-5 w-5 lg:h-6 lg:w-6 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-xs lg:text-sm text-foreground">{selectedSavedCard.display_name}</h3>
                        {selectedSavedCard.method_details?.last4 && (
                          <p className="text-[10px] lg:text-xs text-muted-foreground">
                            •••• {selectedSavedCard.method_details.last4}
                          </p>
                        )}
                      </div>
                    </div>

                      {/* Show Pay button first, CVV input only if 410 error */}
                      {!needsCvvRevalidation ? (
                        // Default: Try checkout without CVV
                        <div className="space-y-2 lg:space-y-4">
                          <Button
                            onClick={handleSavedCardCheckout}
                            disabled={isProcessing}
                            className="w-full h-8 lg:h-11 bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 text-white font-black rounded-lg shadow-xl hover:shadow-2xl border border-green-400/30 hover:border-green-300/50 transition-all duration-200 text-[10px] lg:text-sm uppercase tracking-wide relative overflow-hidden group/btn"
                          >
                            {isProcessing ? (
                              <span className="flex items-center gap-1.5 lg:gap-2">
                                <span className="animate-spin">⏳</span>
                                <span className="text-[9px] lg:text-sm">Processing...</span>
                              </span>
                            ) : (
                              <span className="relative z-10">Pay ${(amountInCents / 100).toFixed(2)}</span>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
                          </Button>
                        </div>
                      ) : (
                        // CVV revalidation required (410 error from checkout)
                        <div className="space-y-2 lg:space-y-4">
                          <div className="p-2 lg:p-4 bg-blue-500/20 border border-blue-400/30 rounded-lg">
                            <p className="text-[10px] lg:text-sm font-black text-blue-300 mb-1 lg:mb-2 uppercase tracking-wide">
                              Card Verification Required
                            </p>
                            <p className="text-[9px] lg:text-xs text-blue-300/80 mb-1 lg:mb-2">
                              Your card requires CVV verification to complete this payment.
                            </p>
                            <p className="text-[9px] lg:text-xs text-blue-300/70">
                              <strong>Note:</strong> CVV must be 3 digits for Visa/Mastercard/Discover, or 4 digits for American Express.
                            </p>
                          </div>
                          
                          <div className="space-y-1.5 lg:space-y-2">
                            <Label htmlFor="cvv" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">
                              CVV
                              <span className="text-[9px] lg:text-xs text-muted-foreground ml-1 lg:ml-2 font-normal">
                                ({selectedSavedCard.method_details?.type === 'AMEX' || selectedSavedCard.method_details?.type === 'amex' || selectedSavedCard.method_details?.brand?.toUpperCase() === 'AMEX' ? '4 digits' : '3 digits'})
                              </span>
                            </Label>
                            <div 
                              className="coinflow-cvv-wrapper mt-1 lg:mt-2"
                              style={{
                                height: '1.75rem',
                                borderRadius: '0.5rem',
                                border: '1px solid hsl(var(--input))',
                                backgroundColor: 'hsl(var(--background))',
                                transition: 'all 0.2s',
                                overflow: 'hidden'
                              }}
                            >
                              <CoinflowCvvOnlyInput
                                ref={coinflowCvvOnlyRef}
                                merchantId={merchantId}
                                token={selectedSavedCard.token}
                                cardType={getCardTypeEnum(selectedSavedCard.method_details.type)}
                                env={import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'prod' as CoinflowEnvs : 'sandbox' as CoinflowEnvs}
                                debug={import.meta.env.VITE_COINFLOW_ENVIRONMENT !== 'production'}
                                css={{
                                  base: 'height: 1.75rem; width: 100%; border-radius: 0.5rem; border: none; background-color: transparent; color: hsl(var(--foreground)); padding: 0.25rem 0.5rem; font-size: 0.625rem; line-height: 1rem; outline: none; transition: all 0.2s; box-sizing: border-box; caret-color: hsl(var(--foreground));',
                                  focus: 'outline: none; border: none; box-shadow: none;',
                                  error: 'border-color: hsl(0, 84%, 60%);',
                                  cvv: {
                                    base: 'height: 1.75rem; width: 100%; border-radius: 0.5rem; border: none; background-color: transparent; color: hsl(var(--foreground)); padding: 0.25rem 0.5rem; font-size: 0.625rem; line-height: 1rem; outline: none; transition: all 0.2s; box-sizing: border-box; caret-color: hsl(var(--foreground));',
                                    focus: 'outline: none; border: none; box-shadow: none;',
                                    error: 'border-color: hsl(0, 84%, 60%);',
                                  },
                                }}
                                origins={[window.location.origin]}
                              />
                            </div>
                            <p className="text-[9px] lg:text-xs text-muted-foreground mt-0.5 lg:mt-1">
                              Enter your CVV to verify your card
                            </p>
                          </div>

                          <Button
                            onClick={handleCvvRevalidation}
                            disabled={isProcessing || !cvvInputReady}
                            className="w-full h-8 lg:h-11 bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 text-white font-black rounded-lg shadow-xl hover:shadow-2xl border border-green-400/30 hover:border-green-300/50 transition-all duration-200 text-[10px] lg:text-sm uppercase tracking-wide relative overflow-hidden group/btn"
                          >
                            {isProcessing ? (
                              <span className="flex items-center gap-1.5 lg:gap-2">
                                <span className="animate-spin">⏳</span>
                                <span className="text-[9px] lg:text-sm">Processing CVV...</span>
                              </span>
                            ) : (
                              <span className="relative z-10">Verify and Pay ${(amountInCents / 100).toFixed(2)}</span>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
                          </Button>
                          {!cvvInputReady && (
                            <p className="text-[9px] lg:text-xs text-muted-foreground text-center mt-1 lg:mt-2">
                              Please wait for the CVV field to load...
                            </p>
                          )}
                          {isProcessing && (
                            <p className="text-[9px] lg:text-xs text-muted-foreground text-center mt-1 lg:mt-2">
                              Please wait while we verify your CVV. This may take a few seconds...
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                </div>
            </div>
          )}

            {/* Payment Method Selection or Form */}
            {/* Wait for BOTH checkoutSessionLoading to be false AND checkoutSessionId to be set */}
            {/* This ensures wallet buttons receive a valid checkoutSessionId and don't create duplicate transactions */}
            {!challengeData && !selectedSavedCard && !selectedMethod && !isProcessing && checkoutSessionLoading ? (
              <div className="flex flex-col items-center justify-center py-8 lg:py-12">
                <div className="relative mb-3">
                  <div 
                    className="absolute inset-0 blur-2xl opacity-30"
                    style={{
                      background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
                      width: 120,
                      height: 120,
                      left: -30,
                      top: -30,
                    }}
                  />
                  <img
                    src={logoSrc}
                    alt="Gold Machine"
                    width={60}
                    height={60}
                    className="relative z-10 animate-pulse object-contain"
                    style={{ animationDuration: '2s' }}
                  />
                </div>
                <p className="text-[10px] lg:text-sm font-medium text-amber-400/80 mb-2">Loading payment options...</p>
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-amber-500/60 animate-pulse"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            ) : !challengeData && !selectedSavedCard && !selectedMethod && !isProcessing && checkoutSessionError ? (
              <CheckoutSessionErrorState
                error={checkoutSessionError}
                onClose={onClose}
                onRetry={() => {
                  checkoutSessionInitialized.current = false;
                  setCheckoutSessionError(null);
                  setCheckoutSessionLoading(true);
                }}
                compact
              />
            ) : !challengeData && !selectedSavedCard && !selectedMethod && !isProcessing && checkoutSessionId ? (
              <>
                <PaymentMethodSelector
                  key={`${refreshKey}-${checkoutSessionId}`}
                  onSelect={handleMethodSelect}
                  onSelectSavedCard={handleSavedCardSelect}
                  packageId={itemId}
                  scheduledOfferId={isScheduledOffer ? itemId : undefined}
                  amount={amountInCents}
                  refreshKey={refreshKey}
                  packageTitle={packageTitle}
                  onGooglePaySuccess={handlePaymentSuccess}
                  onGooglePayError={handlePaymentError}
                  onApplePaySuccess={handlePaymentSuccess}
                  onApplePayError={handlePaymentError}
                  checkoutSessionId={checkoutSessionId}
                  onCashAppCheckout={handleCashAppCheckout}
                  onCryptoCheckout={handleCryptoCheckout}
                />
                
              </>
            ) : !challengeData && !selectedSavedCard && isProcessing && !selectedMethod ? (
              <div className="flex flex-col items-center justify-center py-8 lg:py-12">
                <div className="relative mb-3">
                  <div 
                    className="absolute inset-0 blur-2xl opacity-30"
                    style={{
                      background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
                      width: 120,
                      height: 120,
                      left: -30,
                      top: -30,
                    }}
                  />
                  <img
                    src={logoSrc}
                    alt="Gold Machine"
                    width={60}
                    height={60}
                    className="relative z-10 animate-pulse object-contain"
                    style={{ animationDuration: '2s' }}
                  />
                </div>
                <p className="text-[10px] lg:text-sm font-medium text-amber-400/80 mb-2">Processing payment...</p>
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-amber-500/60 animate-pulse"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            ) : !challengeData && !selectedSavedCard ? (
              <div className="space-y-2 lg:space-y-4">
                {/* Back Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-7 lg:h-8 px-2 lg:px-3 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1" />
                  <span className="text-[10px] lg:text-sm font-medium">Back to payment methods</span>
                </Button>

              {/* Payment Forms */}
              {selectedMethod === 'card' && (
                <CardPaymentForm
                  amount={amountInCents}
                  packageId={itemId}
                  scheduledOfferId={isScheduledOffer ? itemId : undefined}
                  packageTitle={packageTitle}
                  promoCode={promoCode}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  checkoutSessionId={checkoutSessionId}
                />
              )}

                {selectedMethod === 'apple_pay' && (
                  <div className="space-y-2 lg:space-y-4">
                    <ApplePayButton
                      amount={amountInCents}
                      packageId={itemId}
                      scheduledOfferId={isScheduledOffer ? itemId : undefined}
                      packageTitle={packageTitle}
                      merchantId={merchantId}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      checkoutSessionId={checkoutSessionId}
                    />
                    <p className="text-[10px] lg:text-sm text-muted-foreground text-center">
                      Or use a different payment method
                    </p>
                    <Button
                      variant="ghost"
                      onClick={handleBack}
                      className="text-[10px] lg:text-sm text-muted-foreground hover:text-foreground w-full"
                    >
                      Choose another payment method
                    </Button>
                  </div>
                )}

                {/* Google Pay - TEMPORARILY DISABLED for testing */}
                {/* TODO: Re-enable once Coinflow Google Pay issue is resolved */}
                {false && selectedMethod === 'google_pay' && (
                  <div className="space-y-2 lg:space-y-4">
                    <GooglePayButton
                      amount={amountInCents}
                      packageId={itemId}
                      scheduledOfferId={isScheduledOffer ? itemId : undefined}
                      packageTitle={packageTitle}
                      merchantId={merchantId}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      checkoutSessionId={checkoutSessionId}
                    />
                    <p className="text-[10px] lg:text-sm text-muted-foreground text-center">
                      Or use a different payment method
                    </p>
                    <Button
                      variant="ghost"
                      onClick={handleBack}
                      className="text-[10px] lg:text-sm text-muted-foreground hover:text-foreground w-full"
                    >
                      Choose another payment method
                    </Button>
                  </div>
                )}

                {selectedMethod === 'ach' && (
                  <ACHPaymentForm
                    amount={amountInCents}
                    packageId={itemId}
                    scheduledOfferId={isScheduledOffer ? itemId : undefined}
                    packageTitle={packageTitle}
                    promoCode={promoCode}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    checkoutSessionId={checkoutSessionId}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

